import { DashboardState, ESdChangeReason, LeftPanelType } from "@kuika/kuika-cl-model";
import { IBranchDto } from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import { type AxiosResponse } from "axios";
import BranchingService from "../../../../../services/branching-service";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { sortBranchesMasterFirst } from "../../../../../shared/utility/branch-helpers";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import KuikaButton, { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEntityPicker, { KuikaPickerItem } from "../../../../ui-kit/kuika-entity-picker/kuika-entity-picker";
import KuikaOverflowText from "../../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSmallSpinner from "../../../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { type ToastId, toastService } from "../../../../ui-kit/kuika-toast/toast-service";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import ActivateBranchingModal from "../../../branching/activate-branching-modal";
import {
  BRANCH_NAME_MAX_LENGTH,
  IBranchNameValidationResult,
  validateBranchName
} from "../../../branching/branch-name-validation";
import { isBranchingUIEnabled } from "../../../branching/branching-feature-gate";
import CreateBranchModal from "../../../branching/create-branch-modal";
import CreateBranchUncommittedModal from "../../../branching/create-branch-uncommitted-modal";
import DeleteBranchModal from "../../../branching/delete-branch-modal";
import RenameBranchModal from "../../../branching/rename-branch-modal";
import SwitchBranchWarningModal from "../../../branching/switch-branch-warning-modal";
import "./branch-selector.scss";

/** A branch as the switcher panel lists it: the picker's id and name, with the branch it stands for. */
interface IBranchPickerItem extends KuikaPickerItem {
  branch: IBranchDto;
}

/** What the switch's toast says when the branch could not be switched to. */
const describeSwitchFailure = (reason: unknown): string => {
  if (reason instanceof Error && reason.message) return reason.message;
  return "The branch could not be switched.";
};

export interface IBranchSelectorButtonProps {}

export interface IBranchSelectorButtonState {
  isPopoverVisible: boolean;
  showCreateBranchModal: boolean;
  showRenameBranchModal: boolean;
  newBranchName: string;
  selectedSourceBranch: string;
  branchToRename: IBranchDto | null;
  renameBranchName: string;
  inProgress: boolean;
  showSwitchWarning: boolean;
  pendingSwitchBranch: IBranchDto | null;
  switchStagedCount: number;
  // Branch whose switch is currently being resolved (staged-check in flight).
  // Drives the row spinner and guards against double-click double-warnings.
  switchingBranchId: string | null;
  showDeleteModal: boolean;
  branchToDelete: IBranchDto | null;
  showUncommittedModal: boolean;
  uncommittedCount: number;
  uncommittedCommitMessage: string;
  /** Popover's branch filter ("Search branches…"); reset whenever the popover closes. */
  branchSearchText: string;
  /** Version-control activation confirmation (the pre-activation trigger chip opens it). */
  showActivateModal: boolean;
  activateInProgress: boolean;
  /**
   * Where the open dialog's commit stands — activate, create, rename, delete or the switch past
   * uncommitted changes; only one of them is ever open. Owned by `SaveStatus`, never set
   * directly.
   */
  commitStatus: KuikaButtonStatus;
  /** Which of the uncommitted-changes dialog's two buttons ran the create, so it carries the status. */
  uncommittedAction: "commit" | "without";
}

/**
 * Row-1 branch control (KOA-16942). Replaces the old toolbar "Branching" tab and the
 * row-2 branch selector: a bordered "⑂ <branch> ▾" box whose dropdown handles the
 * routine branch management (switch / new / rename / protect / delete) inline, while
 * the heavy version-control workspace (activate, commits, merge, requests) opens the
 * full branching page. Branch-management handlers + modals mirror BranchingPanel.
 */
class BranchSelectorButton extends PureSdComponent<
  IBranchSelectorButtonProps & IWithSDProps,
  IBranchSelectorButtonState
> {
  state: IBranchSelectorButtonState = {
    isPopoverVisible: false,
    showCreateBranchModal: false,
    showRenameBranchModal: false,
    newBranchName: "",
    selectedSourceBranch: "",
    branchToRename: null,
    renameBranchName: "",
    inProgress: false,
    showSwitchWarning: false,
    pendingSwitchBranch: null,
    switchStagedCount: 0,
    switchingBranchId: null,
    showDeleteModal: false,
    branchToDelete: null,
    showUncommittedModal: false,
    uncommittedCount: 0,
    uncommittedCommitMessage: "",
    branchSearchText: "",
    showActivateModal: false,
    activateInProgress: false,
    commitStatus: "idle",
    uncommittedAction: "commit"
  };

  /**
   * The dialogs' commit state machine. A dialog whose result shows once it has gone (activate,
   * rename, the uncommitted-changes switch) holds its ✓ and then closes; one whose result is a
   * toast (delete) goes back to idle as it closes; create holds its ✓ and keeps its toast — the
   * new branch is not on screen.
   */
  private readonly commitStatus: SaveStatus = new SaveStatus((commitStatus: KuikaButtonStatus): void => {
    this.setState({ commitStatus });
  });

  componentWillUnmount(): void {
    this.commitStatus.dispose();
  }

  private closePopover = (): void => this.setState({ isPopoverVisible: false, branchSearchText: "" });

  // ── Activate version control (pre-activation trigger chip → confirmation → initialize) ──

  private handleCloseActivateModal = (): void => {
    if (this.state.activateInProgress || !this.commitStatus.canStart) return;
    this.setState({ showActivateModal: false });
  };

  /**
   * Same initialization flow as the Branching page's activate empty-state
   * (branching-canvas.handleActivateBranching): initialize server-side, adopt the returned
   * master branch, and reload the open screen so it re-renders under version control.
   */
  private handleConfirmActivateBranching = async (): Promise<void> => {
    if (
      this.props.sd.currentBranch ||
      this.state.activateInProgress ||
      !this.commitStatus.canStart ||
      !isBranchingUIEnabled()
    ) {
      return;
    }
    this.commitStatus.start();
    this.setState({ activateInProgress: true });
    try {
      const res = await BranchingService.InitializeBranching();
      if (res.status === 200 && res.data) {
        const masterBranchId: string = res.data.id;
        // The ✓ is held on ACTIVATE first: adopting the branch swaps this whole control for the
        // branch chip, and the dialog would go with it before its confirmation was seen.
        this.commitStatus.succeed((): void => {
          this.props.sd.setCurrentBranch(masterBranchId);
          this.props.sd.loadScreen(this.props.sd.sdId, true, true);
          this.setState({ showActivateModal: false });
        });
      } else {
        this.commitStatus.fail();
      }
    } catch {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to activate Version Control. Please try again.");
    } finally {
      this.setState({ activateInProgress: false });
    }
  };

  // Navigate to the full version-control page (activate empty-state / commits / merge / requests).
  private goToBranchingPage = (): void => {
    this.closePopover();
    this.props.sd.setActiveBranchingPanel(LeftPanelType.Branching);
  };

  // ── Switch ──

  selectBranch = async (branchId: string): Promise<void> => {
    const currentBranchId: string | undefined = this.props.sd.currentBranch?.id;
    if (branchId === currentBranchId) {
      this.closePopover();
      return;
    }
    // In-flight guard: a rapid second click (because nothing looked like it happened)
    // must not fire a second staged-check / warning. One resolution at a time.
    if (this.state.switchingBranchId) {
      return;
    }
    // Instant feedback: mark the clicked row as resolving *before* the async staged
    // check, so the click registers immediately (row spinner) instead of feeling frozen.
    this.setState({ switchingBranchId: branchId });
    if (currentBranchId) {
      try {
        const stagedRes = await BranchingService.GetStagedEntities(currentBranchId);
        const stagedCount: number = stagedRes?.data?.length ?? 0;
        if (stagedCount > 0) {
          const targetBranch: IBranchDto | null = this.props.sd.branches?.find((b) => b.id === branchId) ?? null;
          this.setState({
            showSwitchWarning: true,
            pendingSwitchBranch: targetBranch,
            switchStagedCount: stagedCount,
            isPopoverVisible: false,
            switchingBranchId: null
          });
          return;
        }
      } catch {
        // If the staged check fails, fall through and switch (don't block the user).
      }
    }
    this.performBranchSwitch(branchId);
  };

  /**
   * A switch straight from the list reloads the project under the other branch with no dialog on
   * screen, so one toast carries it from "Switching" to its outcome, as the branching page's list
   * does. When the service has already reported a failure, the progress toast steps aside.
   */
  private performBranchSwitch = (branchId: string): void => {
    const branch: IBranchDto | undefined = this.props.sd.branches?.find((b) => b.id === branchId);
    this.setState({ isPopoverVisible: false, switchingBranchId: null });
    let toastId: ToastId | null = null;
    const work: Promise<void> = (async (): Promise<void> => {
      let isSwitched: boolean;
      try {
        const res: AxiosResponse<boolean> = await BranchingService.UpdateUserBranchingState(branchId);
        isSwitched = !!res.data;
      } catch (error: unknown) {
        // The service has already reported the failure in its own toast; this one steps aside
        // before the rejection reaches it, rather than say the same thing twice.
        if (toastId !== null) toastService.dismiss(toastId);
        throw error;
      }
      if (!isSwitched) {
        throw new Error(`Could not switch to ${branch?.name ?? "the branch"}.`);
      }
      await this.props.sd.setCurrentBranch(branchId);
    })();
    toastId = toastService.promise(work, {
      loading: `Switching to ${branch?.name ?? "the branch"}…`,
      success: `Switched to branch: ${branch?.name}. Reloading project. Please wait...`,
      error: describeSwitchFailure
    });
  };

  /**
   * The switch past uncommitted changes runs from their warning dialog, which stays up until the
   * switch has landed: SWITCH ANYWAY holds its ✓, the dialog goes, and then the branch is adopted —
   * the header chip taking the new branch is the result, so there is no toast. A refused switch
   * keeps the dialog open on "Try again"; the service has already said why.
   */
  handleConfirmSwitch = async (): Promise<void> => {
    const branch: IBranchDto | null = this.state.pendingSwitchBranch;
    if (!branch || !this.commitStatus.canStart) return;
    this.commitStatus.start();
    try {
      const res: AxiosResponse<boolean> = await BranchingService.UpdateUserBranchingState(branch.id);
      if (!res.data) {
        this.commitStatus.fail();
        return;
      }
      this.commitStatus.succeed(async (): Promise<void> => {
        this.setState({ showSwitchWarning: false, pendingSwitchBranch: null });
        try {
          await this.props.sd.setCurrentBranch(branch.id);
        } catch (error: unknown) {
          // Runs from succeed()'s timer, after the dialog (and its button's fail state) is gone and
          // the try/catch below has returned — so the reload's failure is reported as a toast.
          KMainFunctions.exceptionHandler(error);
        }
      });
    } catch {
      this.commitStatus.fail();
    }
  };

  handleCancelSwitch = (): void => {
    if (!this.commitStatus.canStart) return;
    this.setState({ showSwitchWarning: false, pendingSwitchBranch: null, switchingBranchId: null });
  };

  /**
   * A branch's marks after its name: the protected lock (a permission signal — commit here, or
   * view-only and propose through a merge request) and, while a switch to it is resolving, the
   * spinner. The CURRENT badge and the "…" menu are the picker's own.
   */
  private renderBranchTrailing = (branch: IBranchDto): React.ReactNode => {
    const isSwitching: boolean = this.state.switchingBranchId === branch.id;
    if (!branch.isProtected && !isSwitching) return null;
    return (
      <>
        {branch.isProtected && (
          <KuikaTooltip
            content={
              branch.permissions?.canMergeInto
                ? "Protected branch — you can commit here"
                : "Protected branch — view-only for you; propose changes via a merge request"
            }
          >
            <span className="kuika-branch-selector__lock">
              <KuikaSvg name={branch.permissions?.canMergeInto ? "lock-open" : "lock"} width={12} height={12} />
            </span>
          </KuikaTooltip>
        )}
        {isSwitching && <KuikaSmallSpinner />}
      </>
    );
  };

  /** The row's "…" menu. Hidden while a switch to that branch is resolving, so the spinner sits alone. */
  private renderBranchMenu = (branch: IBranchDto, onOpenChange: (open: boolean) => void): React.ReactNode => {
    if (this.state.switchingBranchId === branch.id) return null;
    return (
      <KuikaDropdownMenu
        options={this.branchMenu(branch)}
        overlayClassName="kuika-branch-selector__branch-menu"
        placement="bottomRight"
        setOpen={onOpenChange}
        closeOnScroll
      >
        <button type="button" className="kuika-picker__row-menu" aria-label="Branch actions">
          <KuikaSvg name="dots" width={16} height={16} />
        </button>
      </KuikaDropdownMenu>
    );
  };

  // ── Create ──

  handleCreateNewBranch = (): void => {
    this.setState({
      isPopoverVisible: false,
      showCreateBranchModal: true,
      newBranchName: "",
      selectedSourceBranch: this.props.sd.currentBranch?.id || ""
    });
  };

  handleCreateNewBranchFrom = (branch: IBranchDto): void => {
    const sanitizedSource: string = (branch.name ?? "").replace(/[^A-Za-z0-9]/g, "");
    const suggested: string = `${sanitizedSource}copy`.slice(0, BRANCH_NAME_MAX_LENGTH);
    this.setState({
      isPopoverVisible: false,
      showCreateBranchModal: true,
      newBranchName: suggested,
      selectedSourceBranch: branch.id
    });
  };

  handleCreateBranchSubmit = async (): Promise<void> => {
    const { newBranchName, selectedSourceBranch } = this.state;
    const existingNames: string[] = (this.props.sd.branches ?? []).map((b: IBranchDto) => b.name ?? "");
    const validation: IBranchNameValidationResult = validateBranchName(newBranchName, { existingNames });
    if (!validation.valid) {
      KMainFunctions.displayErrorNotification(validation.error ?? "Invalid branch name.");
      return;
    }
    if (!this.commitStatus.canStart) return;
    this.commitStatus.start();
    this.setState({ inProgress: true });
    try {
      const stagedRes = await BranchingService.GetStagedEntities(selectedSourceBranch);
      const stagedCount: number = stagedRes?.data?.length ?? 0;
      if (stagedCount > 0) {
        // Not a failure: the create waits on a second dialog that asks about the changes.
        this.commitStatus.reset();
        this.setState({
          inProgress: false,
          showUncommittedModal: true,
          uncommittedCount: stagedCount,
          uncommittedCommitMessage: ""
        });
        return;
      }
      await this.doCreateBranch();
    } catch {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to create branch");
      this.setState({ inProgress: false });
    }
  };

  handleUncommittedCommitAndCreate = async (): Promise<void> => {
    const message: string = this.state.uncommittedCommitMessage;
    if (!message.trim() || !this.commitStatus.canStart) {
      if (!message.trim()) KMainFunctions.displayErrorNotification("Please provide a commit message.");
      return;
    }
    this.setState({ uncommittedAction: "commit" });
    await this.commitAndCreateBranch(message);
  };

  handleUncommittedCreateWithout = async (): Promise<void> => {
    if (!this.commitStatus.canStart) return;
    this.setState({ uncommittedAction: "without" });
    await this.doCreateBranch();
  };

  handleCloseUncommittedModal = (): void => {
    if (this.state.inProgress || !this.commitStatus.canStart) return;
    this.setState({ showUncommittedModal: false });
  };

  /**
   * The new branch is created without becoming current and the popover is closed, so nothing on
   * screen shows it: the toast stays, and the dialog holds its ✓ before it goes.
   */
  private doCreateBranch = async (): Promise<void> => {
    const { newBranchName, selectedSourceBranch } = this.state;
    this.commitStatus.start();
    this.setState({ inProgress: true });
    try {
      await this.props.sd.createBranch({ name: newBranchName, parentBranchId: selectedSourceBranch });
      KMainFunctions.displaySuccessNotification("Branch created successfully");
      this.commitStatus.succeed((): void => {
        this.setState({
          showCreateBranchModal: false,
          showUncommittedModal: false,
          newBranchName: "",
          selectedSourceBranch: "",
          uncommittedCommitMessage: ""
        });
      });
    } catch {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to create branch");
    } finally {
      this.setState({ inProgress: false });
    }
  };

  private commitAndCreateBranch = async (commitMessage: string): Promise<void> => {
    const { selectedSourceBranch } = this.state;
    this.commitStatus.start();
    this.setState({ inProgress: true });
    try {
      await BranchingService.CommitStaged(selectedSourceBranch, commitMessage);
      this.props.sd.loadScreen(this.props.sd.sdId, true, true);
      await this.doCreateBranch();
    } catch {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to commit and create branch");
      this.setState({ inProgress: false });
    }
  };

  // ── Rename ──

  handleRenameBranch = (branch: IBranchDto): void => {
    this.setState({
      isPopoverVisible: false,
      showRenameBranchModal: true,
      branchToRename: branch,
      renameBranchName: branch.name
    });
  };

  handleRenameBranchSubmit = async (): Promise<void> => {
    const { branchToRename, renameBranchName } = this.state;
    if (!branchToRename) return;
    const existingNames: string[] = (this.props.sd.branches ?? []).map((b: IBranchDto) => b.name ?? "");
    const validation: IBranchNameValidationResult = validateBranchName(renameBranchName, {
      existingNames,
      currentName: branchToRename.name
    });
    if (!validation.valid) {
      KMainFunctions.displayErrorNotification(validation.error ?? "Invalid branch name.");
      return;
    }
    if (!this.commitStatus.canStart) return;
    this.commitStatus.start();
    this.setState({ inProgress: true });
    try {
      await this.props.sd.renameBranch(branchToRename.id, renameBranchName);
      await this.props.sd.getBranches();
      // The ✓ first; the chip and the popover's list carry the new name once the dialog has gone.
      this.commitStatus.succeed((): void => {
        this.setState({ showRenameBranchModal: false, branchToRename: null, renameBranchName: "" });
      });
    } catch {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to rename branch");
    } finally {
      this.setState({ inProgress: false });
    }
  };

  // ── Protect ──

  handleToggleProtection = async (branch: IBranchDto): Promise<void> => {
    this.closePopover();
    const next: boolean = !branch.isProtected;
    try {
      const res = await BranchingService.SetBranchProtection(branch.id, next);
      if (res?.data) {
        KMainFunctions.displaySuccessNotification(
          next ? `“${branch.name}” is now protected.` : `“${branch.name}” is no longer protected.`
        );
        await this.props.sd.getBranches();
      }
    } catch {
      KMainFunctions.displayErrorNotification("Failed to update branch protection.");
    }
  };

  // ── Merge (hands off to the full page) ──

  handleMergeBranch = async (branch: IBranchDto): Promise<void> => {
    this.closePopover();
    try {
      if (branch?.id && branch.id !== this.props.sd.currentBranch?.id) {
        const res = await BranchingService.UpdateUserBranchingState(branch.id);
        if (res?.data) this.props.sd.setCurrentBranch(branch.id);
      }
      BranchingService.RequestStartMerge();
      this.props.sd.setDashboardState(DashboardState.branching);
    } catch {
      KMainFunctions.displayErrorNotification("Failed to open merge flow");
    }
  };

  // ── Delete ──

  handleDeleteBranch = (branch: IBranchDto): void => {
    this.setState({ isPopoverVisible: false, showDeleteModal: true, branchToDelete: branch });
  };

  handleCloseDeleteModal = (): void => {
    if (this.state.inProgress || !this.commitStatus.canStart) return;
    this.setState({ showDeleteModal: false, branchToDelete: null });
  };

  handleConfirmDeleteBranch = async (): Promise<void> => {
    const branch: IBranchDto | null = this.state.branchToDelete;
    if (!branch || !this.commitStatus.canStart) return;
    this.commitStatus.start();
    this.setState({ inProgress: true });
    try {
      await this.props.sd.deleteBranch(branch.id);
      if (this.props.sd.currentBranch?.id === branch.id) {
        const masterBranch: IBranchDto | undefined = this.props.sd.branches?.find(
          (b) => b.name?.toLowerCase() === "master"
        );
        if (masterBranch) {
          await BranchingService.UpdateUserBranchingState(masterBranch.id);
          await this.props.sd.setCurrentBranch(masterBranch.id);
        }
      }
      // The branch and the dialog are gone: the toast says so, not a ✓.
      this.commitStatus.reset();
      KMainFunctions.displaySuccessNotification(`Branch "${branch.name}" has been deleted.`);
      this.setState({ showDeleteModal: false, branchToDelete: null });
    } catch {
      this.commitStatus.fail();
      KMainFunctions.displayErrorNotification("Failed to delete branch.");
    } finally {
      this.setState({ inProgress: false });
    }
  };

  // ── Per-branch action menu ──

  private branchMenu = (branch: IBranchDto) => {
    const perms = branch.permissions;
    const isMaster: boolean = branch.name?.toLowerCase() === "master";
    return [
      {
        key: "rename",
        label: "Rename branch",
        icon: <KuikaSvg width={16} height={16} name="edit" />,
        onClick: () => this.handleRenameBranch(branch),
        hidden: isMaster || perms?.canRename === false
      },
      {
        key: "create-from",
        label: "Create new from this branch",
        icon: <KuikaSvg width={16} height={16} name="copy" />,
        onClick: () => this.handleCreateNewBranchFrom(branch),
        hidden: perms?.canCreateBranch === false
      },
      {
        key: "protection",
        label: branch.isProtected ? "Unprotect branch" : "Protect branch",
        icon: <KuikaSvg width={16} height={16} name="lock" />,
        onClick: () => this.handleToggleProtection(branch),
        hidden: perms?.canToggleProtection !== true
      },
      {
        key: "merge",
        label: "Merge into another branch",
        icon: <KuikaSvg width={16} height={16} name="git-merge" />,
        onClick: () => this.handleMergeBranch(branch),
        group: 1
      },
      {
        key: "delete",
        label: "Delete branch",
        icon: <KuikaSvg width={16} height={16} name="trash" />,
        onClick: () => this.handleDeleteBranch(branch),
        danger: true,
        group: 2,
        hidden: isMaster || perms?.canDelete === false
      }
    ];
  };

  private canCreateBranch = (): boolean =>
    this.props.sd.currentBranch?.permissions?.canCreateBranch ??
    this.props.sd.branches?.[0]?.permissions?.canCreateBranch ??
    true;

  /**
   * Popover body: the switcher panel every other editor opens (KuikaEntityPicker — the screen
   * selector's): search with "+ new branch" beside it, the branches with the checked-out one marked
   * CURRENT, and a footer counting them that leads to the full version-control page. It used to open
   * on a "Current Branch" card repeating the name the trigger already shows. The behaviours are the
   * same: switch with the staged-changes warning, the per-row menu, the permission-gated create.
   */
  private renderPopoverContent = (): React.ReactNode => {
    const currentBranch: IBranchDto | undefined = this.props.sd.currentBranch;
    const branches: IBranchDto[] = sortBranchesMasterFirst(this.props.sd.branches ?? []);
    const needle: string = KMainFunctions.turkishCharsToEnglish(this.state.branchSearchText).toLowerCase().trim();
    const visibleBranches: IBranchDto[] = needle
      ? branches.filter((branch: IBranchDto) =>
          KMainFunctions.turkishCharsToEnglish(branch.name ?? "")
            .toLowerCase()
            .includes(needle)
        )
      : branches;
    const canCreate: boolean = this.canCreateBranch();
    const items: IBranchPickerItem[] = visibleBranches.map((branch: IBranchDto): IBranchPickerItem => ({
      id: branch.id,
      name: branch.name ?? "",
      branch
    }));
    return (
      <KuikaEntityPicker<IBranchPickerItem>
        className="kuika-branch-selector"
        items={items}
        currentId={currentBranch?.id}
        isFiltering={!!needle}
        query={this.state.branchSearchText}
        onQueryChange={(query: string): void => this.setState({ branchSearchText: query })}
        searchPlaceholder="Search branches..."
        searchTestId="branchSelectorSearch"
        createControl={
          <KuikaTooltip content={canCreate ? "New branch" : "You don't have permission to create branches"}>
            <button
              type="button"
              className="kuika-picker__add"
              disabled={!canCreate}
              onClick={this.handleCreateNewBranch}
              data-testid="branchSelectorNewBranch"
              aria-label="New branch"
            >
              <KuikaSvg name="plus" width={16} height={16} />
            </button>
          </KuikaTooltip>
        }
        renderItemIcon={(): React.ReactNode => <KuikaSvg name="git-branch" width={16} height={16} />}
        renderItemTrailing={(item: IBranchPickerItem): React.ReactNode => this.renderBranchTrailing(item.branch)}
        onSelect={(item: IBranchPickerItem): void => {
          this.selectBranch(item.id);
        }}
        renderItemMenu={(item: IBranchPickerItem, onOpenChange: (open: boolean) => void): React.ReactNode =>
          this.renderBranchMenu(item.branch, onOpenChange)
        }
        itemTestId={(item: IBranchPickerItem): string => `branchSelectorRow-${item.id}`}
        itemNameTestId="branchSelectorRowName"
        emptyText="No branch matched"
        footerCount={`${branches.length} ${branches.length === 1 ? "branch" : "branches"}`}
        manageLabel="Manage all branches"
        onManage={this.goToBranchingPage}
        manageTestId="branchSelectorManageAll"
      />
    );
  };

  render() {
    if (!isBranchingUIEnabled()) return null;

    const existingBranchNames: string[] = (this.props.sd.branches ?? []).map((b: IBranchDto) => b.name ?? "");
    const currentBranch: IBranchDto | undefined = this.props.sd.currentBranch;
    const triggerClassName: string = "kuika-branch-selector__trigger";

    // Version control not active yet → the segment is the compact ACTIVATE chip (Figma node
    // 1863:109298: a framed git-branch square, neutral at rest and blue on hover — the colours
    // live in branch-selector.scss, NOT on a KuikaSvg `color` prop, since that writes
    // `--kuika-svg-color` inline and no hover rule could then override it). Clicking asks for
    // confirmation first — activation is cheap to run but heavy to walk back — and only the
    // modal's ACTIVATE runs it.
    if (!currentBranch) {
      return (
        <>
          <KuikaTooltip content="Activate version control">
            <KuikaButton
              type="secondary"
              color="neutral"
              size="md"
              isIcon
              className={`${triggerClassName} kuika-branch-selector__trigger--activate`}
              data-testid="branchSelectorActivate"
              aria-label="Activate version control"
              onClick={() => this.setState({ showActivateModal: true })}
            >
              <span className="kuika-branch-selector__activate-chip">
                <KuikaSvg name="git-branch" width={18} height={18} />
              </span>
            </KuikaButton>
          </KuikaTooltip>
          <ActivateBranchingModal
            showModal={this.state.showActivateModal}
            inProgress={this.state.activateInProgress}
            status={this.state.commitStatus}
            onClose={this.handleCloseActivateModal}
            onActivate={this.handleConfirmActivateBranching}
          />
        </>
      );
    }

    return (
      <>
        <KuikaPopover
          visible={this.state.isPopoverVisible}
          onVisibleChange={(v) => {
            // Closing clears the filter so the next open starts from the full list.
            this.setState({ isPopoverVisible: v, branchSearchText: v ? this.state.branchSearchText : "" });
          }}
          position="bottom"
          align="end"
          sideOffset={8}
          collisionPadding={12}
          hideCloseIcon
          className="kuika-branch-selector__popover"
          content={this.renderPopoverContent()}
        >
          {/* The open state is OUR modifier, not KuikaButton's `active` (KOA-18355): the neutral
              variant's `active` look is a grey fill with `!important`, which would drag this pill
              back to neutral the moment its popover opened — the opposite of the design, where an
              open popover is the strongest engagement and must stay blue like hover. Styling it
              through `--open` keeps that override out of the shared primitive entirely. */}
          <KuikaButton
            type="secondary"
            color="neutral"
            size="sm"
            className={`${triggerClassName} ${OVERFLOW_REVEAL_HOST_CLASS}${
              this.state.isPopoverVisible ? " kuika-branch-selector__trigger--open" : ""
            }`}
            data-testid="branchSelectorButton"
          >
            <div className="kuika-flex-center" style={{ gap: 6 }}>
              {/* 18px, the header's icon size — the chevron beside it stays 12: it is a caret
                  marking the dropdown, not a second icon competing with this one. */}
              <KuikaSvg name="branching" type="topbar-icons" width={18} height={18} />
              {/* The pill is capped at 200px, so a long branch name is routinely cut. It reveals
                  itself rather than ending in an ellipsis: the host class above puts the slide on
                  the whole pill's hover, so pointing at the control — not at the word — is what
                  plays it. `title` stays as the fallback for anyone who cannot hover; the styled
                  tooltip is gone, because it would pop up over the pill saying the same name the
                  label is in the middle of showing. */}
              <KuikaOverflowText
                className="kuika-branch-selector__label"
                title={currentBranch.name}
                data-testid="branchSelectorCurrentName"
              >
                {currentBranch.name}
              </KuikaOverflowText>
              <KuikaSvg
                name={this.state.isPopoverVisible ? "chevron-up" : "chevron-down"}
                type="arrows"
                width={12}
                height={12}
              />
            </div>
          </KuikaButton>
        </KuikaPopover>

        <CreateBranchModal
          showModal={this.state.showCreateBranchModal}
          branchName={this.state.newBranchName}
          selectedSourceBranch={this.state.selectedSourceBranch}
          branchOptions={sortBranchesMasterFirst(this.props.sd.branches ?? []).map((b) => ({
            value: b.id,
            label: b.name
          }))}
          existingBranchNames={existingBranchNames}
          inProgress={this.state.inProgress}
          status={this.state.commitStatus}
          onClose={() => this.commitStatus.canStart && this.setState({ showCreateBranchModal: false })}
          onBranchNameChange={(e) => this.setState({ newBranchName: e.target.value })}
          onSourceBranchChange={(value) => this.setState({ selectedSourceBranch: value as string })}
          onCreateBranch={this.handleCreateBranchSubmit}
        />

        <RenameBranchModal
          showModal={this.state.showRenameBranchModal}
          branchName={this.state.renameBranchName}
          currentBranchName={this.state.branchToRename?.name ?? ""}
          existingBranchNames={existingBranchNames}
          status={this.state.commitStatus}
          onClose={() => this.commitStatus.canStart && this.setState({ showRenameBranchModal: false })}
          onBranchNameChange={(e) => this.setState({ renameBranchName: e.target.value })}
          onRenameBranch={this.handleRenameBranchSubmit}
        />

        <SwitchBranchWarningModal
          showModal={this.state.showSwitchWarning}
          targetBranchName={this.state.pendingSwitchBranch?.name}
          changeCount={this.state.switchStagedCount}
          status={this.state.commitStatus}
          onClose={this.handleCancelSwitch}
          onConfirm={this.handleConfirmSwitch}
        />

        <DeleteBranchModal
          showModal={this.state.showDeleteModal}
          branchName={this.state.branchToDelete?.name}
          inProgress={this.state.inProgress}
          status={this.state.commitStatus}
          onClose={this.handleCloseDeleteModal}
          onDelete={this.handleConfirmDeleteBranch}
        />

        <CreateBranchUncommittedModal
          showModal={this.state.showUncommittedModal}
          changeCount={this.state.uncommittedCount}
          commitMessage={this.state.uncommittedCommitMessage}
          inProgress={this.state.inProgress}
          status={this.state.commitStatus}
          statusAction={this.state.uncommittedAction}
          onClose={this.handleCloseUncommittedModal}
          onCommitMessageChange={(value) => this.setState({ uncommittedCommitMessage: value })}
          onCommitAndCreate={this.handleUncommittedCommitAndCreate}
          onCreateWithoutCommitting={this.handleUncommittedCreateWithout}
        />
      </>
    );
  }

  checkNeedRender = (_: ESdChangeReason): boolean => true;
}

const branchSelectorButton = withSD<IBranchSelectorButtonProps>(BranchSelectorButton);
export { branchSelectorButton as BranchSelectorButton };
